import { supabase } from '../utils/supabaseClient';
import {
  RemoteTable,
  DistantTable,
  SyncRemoteTableRequest,
  RemoteTableListResponse,
  DistantTablesResponse,
} from '../types/remoteTable';

const API_BASE_URL = '/api/integrations';

// Helper to get auth token
const getAuthToken = async (): Promise<string> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No active session');
  }
  return session.access_token;
};

// List available tables from a remote server (not yet synced)
export const listDistantTables = async (
  remoteServerId: string
): Promise<DistantTable[]> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/remote-servers/${remoteServerId}/tables`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to list distant tables');
  }

  const result: DistantTablesResponse = await response.json();
  return result.tables;
};

// Sync a remote table
export const syncRemoteTable = async (
  data: SyncRemoteTableRequest
): Promise<RemoteTable> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/remote-servers/${data.remoteServerId}/tables/sync`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        distantTableName: data.distantTableName,
        localTableName: data.localTableName,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to sync remote table');
  }

  const result = await response.json();
  return {
    ...result,
    createdAt: new Date(result.createdAt),
    updatedAt: new Date(result.updatedAt),
  };
};

// List all synced remote tables
export const listRemoteTables = async (): Promise<RemoteTable[]> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/remote-tables`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to list remote tables');
  }

  const result: RemoteTableListResponse = await response.json();
  return result.tables.map((table) => ({
    ...table,
    createdAt: new Date(table.createdAt),
    updatedAt: new Date(table.updatedAt),
  }));
};

// List synced tables for a specific remote server
export const listRemoteTablesByServer = async (
  remoteServerId: string
): Promise<RemoteTable[]> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/remote-servers/${remoteServerId}/synced-tables`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to list synced tables');
  }

  const result: RemoteTableListResponse = await response.json();
  return result.tables.map((table) => ({
    ...table,
    createdAt: new Date(table.createdAt),
    updatedAt: new Date(table.updatedAt),
  }));
};

// Unsync a remote table
export const unsyncRemoteTable = async (tableId: string): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/remote-tables/${tableId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to unsync remote table');
  }
};
